import type { CommentContextMention } from '@/components/comment-tags'
import type { ApInvoiceDetail } from '@/features/invoices/view-models'
import { firstPoLink, projectForPoLink } from './formatting'

/**
 * Where an invoice's notes live (USA-1180): its project's comments, tagged with the invoice and its POs, so
 * the PO and project pages show them too and link back here.
 */
export type InvoiceNotesContext = {
  invoiceUuid: string
  /** The invoice and its POs, as tags in front of each note. */
  tags: CommentContextMention[]
  /** For @-mention search: the project's people and records come first. */
  projectUuid?: string
}

export function invoiceNotesContext(invoice: ApInvoiceDetail): InvoiceNotesContext | undefined {
  if (!invoice.uuid) return undefined
  const poTags = (invoice.po_links ?? []).flatMap((link) =>
    link.purchase_order?.uuid
      ? [
          {
            entity_type: 'purchase_order',
            entity_uuid: link.purchase_order.uuid,
            display_text: link.purchase_order.po_number || link.po_number_raw || 'PO',
          },
        ]
      : [],
  )
  const uniquePoTags = poTags.filter(
    (tag, index) => poTags.findIndex((other) => other.entity_uuid === tag.entity_uuid) === index,
  )
  return {
    invoiceUuid: invoice.uuid,
    tags: [
      {
        entity_type: 'ap_invoice',
        entity_uuid: invoice.uuid,
        display_text: invoice.invoice_number ? `AP ${invoice.invoice_number}` : 'AP invoice',
      },
      ...uniquePoTags,
    ],
    projectUuid: (invoice.internal_project || projectForPoLink(firstPoLink(invoice)))?.uuid ?? undefined,
  }
}
